import { memo, useEffect } from 'react';
import { useSRZId } from '@/hooks';
import { Mainboard } from '@/pages/HomePage/OrganizationPage/Mainboard';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { sortBy } from 'lodash';
import { PopupContextProvider, DetailsPanelProvider } from '@/contexts';
import { PageLayoutMain, PageLayoutRoot } from '@/components/PageLayout';
import { selectDirty } from '@/components/studio/ScopingEditor/reducer';
import { gql } from '@apollo/client';
import { useOrganizationPageFloorsLazyQuery } from '@/.generated/graphql';
import { useSelector } from 'react-redux';
import DashboardHeader from '@/pages/DashboardHeader';
import { SettingsPage } from '@/pages/Settings';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { NoSpacesAvailable } from '@/components/NoSpacesAvailable';

export const OrganizationPage = () => {
  const { spaceId, roomId, zoneId, buildingId } = useSRZId();
  const history = useHistory();
  const location = useLocation();
  const [getFloors] = useOrganizationPageFloorsLazyQuery();
  const { isUnrestricted, hasAccess, isLoading: isAccessLoading, hasAnyAccess } = useSpaceAccess();

  const isDirty = useSelector(selectDirty);

  useEffect(() => {
    const onbeforeunload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    if (isDirty) {
      window.addEventListener('beforeunload', onbeforeunload);
    }

    return () => window.removeEventListener('beforeunload', onbeforeunload);
  }, [isDirty]);

  // Error handling, and redirect to first accessible space if no space is indicated by query param (& not on building page!)
  useEffect(() => {
    // Wait for access check to complete before redirecting
    if (isAccessLoading) {
      return;
    }

    const isSettingsRoute = location.pathname.startsWith('/settings');
    const isDevicesRoute = location.pathname.startsWith('/devices');
    if (!spaceId && !isSettingsRoute && !isDevicesRoute && !buildingId) {
      // Check if user has any access at all
      if (!hasAnyAccess()) {
        console.log('[OrganizationPage] User has no access to any spaces, not redirecting');
        return;
      }

      // Space id is not defined in url, redirect to first accessible floor
      getFloors().then((res) => {
        const allFloors = res.data?.floors?.data ?? [];

        // Filter floors based on user access
        const accessibleFloors = isUnrestricted
          ? allFloors
          : allFloors.filter((floor) => hasAccess('floor', floor.id));

        console.log(
          `[OrganizationPage] Total floors: ${allFloors.length}, Accessible: ${accessibleFloors.length}`,
        );

        const floor = sortBy(accessibleFloors, 'name')[0]?.id;
        if (floor) {
          console.log(`[OrganizationPage] Redirecting to accessible floor: ${floor}`);
          history.push({ search: `?spaceId=${floor}` });
        } else {
          console.log('[OrganizationPage] No accessible floors found');
        }
      });
    }
  }, [
    spaceId,
    history,
    getFloors,
    location.pathname,
    buildingId,
    isAccessLoading,
    isUnrestricted,
    hasAccess,
    hasAnyAccess,
  ]);

  const isDevicesRoute = location.pathname.startsWith('/devices');
  const hasNoSpaceAccess = !isAccessLoading && !hasAnyAccess();
  const showMainboard = !!(spaceId || roomId || zoneId || buildingId || isDevicesRoute);

  return (
    <PopupContextProvider>
      <DetailsPanelProvider>
        <DashboardHeader />
        <PageLayoutRoot>
          <Sidebar />
          <Switch>
            <Route path={['/settings', '/settings/*']}>
              <SettingsPage />
            </Route>
            <PageLayoutMain>
              <Header />
              {showMainboard && <Mainboard isRoom={!!roomId} isZone={!!zoneId} />}
              {hasNoSpaceAccess && !showMainboard && <NoSpacesAvailable />}
            </PageLayoutMain>
          </Switch>
        </PageLayoutRoot>
      </DetailsPanelProvider>
    </PopupContextProvider>
  );
};

OrganizationPage.queries = {
  Floors: gql`
    query OrganizationPageFloors {
      floors {
        data {
          id: floor_id
          name
        }
      }
    }
  `,
};

export default memo(OrganizationPage);
